import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Fade from '@mui/material/Fade';
import { useTheme } from '@mui/material/styles';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InsightsIcon from '@mui/icons-material/Insights';
import GroupsIcon from '@mui/icons-material/Groups';
import DescriptionIcon from '@mui/icons-material/Description';
import PsychologyIcon from '@mui/icons-material/Psychology';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import Login from './Login';
import ExpandableAssistantLion from './ExpandableAssistantLion';
import ExpandableAdvisorReasoning from './ExpandableAdvisorReasoning';
import EfdAgentChat, { efdAgentFor } from './EfdAgentChat';
import PpiAgentChat from './PpiAgentChat';
import HeyBroInsurance from './HeyBroInsurance';
import ExpandableClient from './ExpandableClient';
import ExpandableMeetingMinutes from './ExpandableMeetingMinutes';
import ChatWidget from './ChatWidget';
import BreachesModal from './breachesModal';
import { closeOnlyViaButton } from './chatMessageStyles';
import UserManagement from './UserManagement';
import ThemeSwitcher from './ThemeSwitcher';
import AudioCards from './audio/AudioCards';
import { API_BASE_URL, removeToken, getToken, getUser, setUser, getAuthHeaders, installAuthInterceptor, setUnauthorizedHandler, isTokenExpired } from './auth';
import { track } from './activity/track';
import './translations';
import { useTranslation } from 'react-i18next';

// Own audio cards (src/audio/): the module — or, until the server has converted
// the stored configuration (src/audio_cards/migration.py), one of the fixed EFD
// cards, or a demo portfolio card next to an assistant (user_admin.current_modules).
const LEGACY_EFD_CARDS = ['asset_under_mgt_mgr', 'kpi_mgr', 'net_new_asset_mgr', 'income_information_mgr', 'new_clients_mgr',
  'asset_under_mgt_adv', 'net_new_asset_adv', 'new_clients_adv', 'contacted_clients_adv'];
const LEGACY_DEMO_CARDS = ['performance', 'region_allocation', 'assetclass_allocation', 'currency_allocation'];
const hasAudioModule = (exp) => exp.includes('audio_cards') || exp.some((tp) => LEGACY_EFD_CARDS.includes(tp))
  || (exp.some((tp) => LEGACY_DEMO_CARDS.includes(tp)) && exp.some((tp) => ['efd_agent', 'ppi_agent', 'efd_survista_agent'].includes(tp)));

// Function expandables → rendered as tiles below the circles, opened full-screen.
const FUNCTION_TILES = {
  chat:            { titleKey: 'chat',            icon: <InsightsIcon /> },
  client_notes:    { titleKey: 'customer_note',   icon: <GroupsIcon /> },
  meeting_minutes: { titleKey: 'meeting_minutes', icon: <DescriptionIcon /> },
  advisor_reasoning: { titleKey: 'advisor_reasoning', icon: <PsychologyIcon /> },
  efd_agent:         { titleKey: 'efd_agent',         icon: <QueryStatsIcon /> },
  heybro_insurance:  { titleKey: 'heybro_insurance',  icon: <HealthAndSafetyIcon /> },
  ppi_agent:         { titleKey: 'ppi_agent',         icon: <DonutSmallIcon /> },
};

// Resting colour for the top-bar controls — a tick brighter than text.secondary
// (0.6) so they read a little more clearly, without going full white.
const TOP_BAR_ICON_COLOR = 'rgba(255, 255, 255, 0.75)';

// Shared styling for the top-bar control icons so their hover behaves identically:
// same colour shift, subtle background highlight, rounded shape and transition.
const TOP_BAR_ICON_SX = {
  color: TOP_BAR_ICON_COLOR,
  borderRadius: '10px',
  transition: 'color 0.2s ease, background-color 0.2s ease',
  '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
};

function App() {
  const theme = useTheme();
  const earlylionLogoSrc = process.env.PUBLIC_URL + (theme.palette.logo === 'light' ? '/earlylion-logo-light.png' : '/earlylion-logo-dark.png');
  const heybroLogoSrc = process.env.PUBLIC_URL + '/heybro-logo.png';
  const bankLogoSrc = theme.palette.bank?.logo ? process.env.PUBLIC_URL + theme.palette.bank.logo : null;
  const storedUser = getUser();
  const [isAuthenticated, setIsAuthenticated] = useState(!!getToken() && !!storedUser);
  const [username, setUsername] = useState(storedUser?.username || '');
  const [firstName, setFirstName] = useState(storedUser?.first_name || '');
  const [uiConfig, setUiConfig] = useState(storedUser?.ui_config || {});
  const [funcModal, setFuncModal] = useState(null);             // 'chat' | 'client_notes' | 'meeting_minutes' | null
  // Module opened — the owner's Activity view (activity/track.js).
  useEffect(() => { if (funcModal) track('module_open', { area: 'app', title: funcModal }); }, [funcModal]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [sessionId, setSessionId] = useState(null);
  const [chatPrefill, setChatPrefill] = useState('');
  const [showUserMgmt, setShowUserMgmt] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Which data set each assistant reads for this user (GET /api/assistants/me):
  // the EFD tile opens the chat of that data set's variant; an assistant
  // without a data set (no access) is not shown at all.
  const [assistants, setAssistants] = useState(null);

  // The session id used to live only in React state, so a reload — or closing
  // the chat by accident — minted a new one and orphaned the conversation that
  // was already stored server-side. Keeping it in sessionStorage means the chat
  // survives that; it is replaced only on an explicit "clear chat", on login and
  // on logout.
  const SESSION_KEY = 'earlylion_chat_session_id';

  const generateSessionId = () => {
    const timestamp = Date.now();
    const randomNumber = Math.floor(Math.random() * 100000) + 1;
    return `${timestamp}-${randomNumber}`;
  };

  // sessionStorage can throw (private mode, blocked storage) — never let that
  // break the chat, just fall back to an in-memory id.
  const storeSessionId = (id) => {
    try { window.sessionStorage.setItem(SESSION_KEY, id); } catch (e) { /* ignore */ }
    return id;
  };

  const restoreOrCreateSessionId = () => {
    try {
      const stored = window.sessionStorage.getItem(SESSION_KEY);
      if (stored) return stored;
    } catch (e) { /* ignore */ }
    return storeSessionId(generateSessionId());
  };

  const startNewSessionId = () => storeSessionId(generateSessionId());

  useEffect(() => { setSessionId(restoreOrCreateSessionId()); }, []);

  const resetSessionId = () => { setSessionId(startNewSessionId()); };

  const handleLogin = (authStatus, userData) => {
    setIsAuthenticated(authStatus);
    if (authStatus && userData) {
      setSessionExpired(false);
      setUsername(userData.username || '');
      setFirstName(userData.first_name || '');
      setUiConfig(userData.ui_config || {});
      setSessionId(startNewSessionId());
    }
  };

  // Surface session expiry instead of letting calls fail silently: a 401 from
  // our API (or an already-expired token) clears the session and routes to the
  // login screen with a notice.
  const handleSessionExpired = useCallback(() => {
    if (!getToken()) return;            // already handled / never logged in
    removeToken();
    setIsAuthenticated(false);
    setShowUserMgmt(false);
    setSessionExpired(true);
  }, []);

  useEffect(() => {
    installAuthInterceptor();
    setUnauthorizedHandler(handleSessionExpired);
    if (isTokenExpired()) handleSessionExpired();
  }, [handleSessionExpired]);

  // The greeting name (and the rest of the user record) comes from the copy
  // cached at login, so a first name edited in the user settings only showed up
  // after the next login. Re-read the record once per mount to keep it honest.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const stored = getUser();
    if (!stored?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/users/${stored.id}`, { headers: getAuthHeaders() });
        if (!res.ok || cancelled) return;
        const fresh = await res.json();
        if (cancelled || !fresh?.username) return;
        setUser(fresh);
        setUsername(fresh.username || '');
        setFirstName(fresh.first_name || '');
        setUiConfig(fresh.ui_config || {});
      } catch (e) { /* offline / hiccup — the cached record stays valid */ }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) { setAssistants(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/assistants/me`, { headers: getAuthHeaders() });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setAssistants(data.assistants || {});
      } catch (e) { /* the tiles fall back to the standard variant */ }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, uiConfig]);

  const handleUserUpdated = () => {
    const stored = localStorage.getItem('user_data');
    if (stored) {
      const u = JSON.parse(stored);
      setUsername(u.username || '');
      setFirstName(u.first_name || '');
      setUiConfig(u.ui_config || {});
    }
  };

  const handleLanguageChange = (event) => { i18n.changeLanguage(event.target.value); };
  const handleModalOpen = () => setIsModalOpen(true);
  const closeFuncModal = () => { setFuncModal(null); setChatPrefill(''); };

  const today = new Date().toLocaleDateString(i18n.language, {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
  });

  function logout() {
    track('logout', { area: 'app', title: 'Abmeldung' });
    removeToken();
    i18n.changeLanguage('de');
    setIsAuthenticated(false);
    setUsername('');
    setFirstName('');
    setUiConfig({});
    setChatPrefill('');
    setFuncModal(null);
    setIsModalOpen(false);
    setSessionId(startNewSessionId());
    setShowUserMgmt(false);
  }

  // Split the configured expandables into the two categories.
  const exp = uiConfig.expandables || [];
  const hasAudioCards = hasAudioModule(exp);
  // efd_survista_agent: the EFD assistant before 2026-10 (one per client).
  const LEGACY_TILES = { chat_audio: 'chat', efd_survista_agent: 'efd_agent' };
  const NO_ACCESS = (tp) => (tp === 'efd_agent' && assistants?.efd && !assistants.efd.variant)
    || (tp === 'ppi_agent' && assistants?.ppi && !assistants.ppi.variant);
  const funcTypes = [...new Set(exp.map(tp => LEGACY_TILES[tp] || tp))]
    .filter(tp => FUNCTION_TILES[tp] && !NO_ACCESS(tp));
  const hasChatWidget = exp.includes('chat_widget');

  const isChatModal = funcModal === 'chat' || funcModal === 'advisor_reasoning' || funcModal === 'efd_agent' || funcModal === 'heybro_insurance' || funcModal === 'ppi_agent';
  // The dossier magazine carries a sidebar + person-card grids — give it a
  // fixed 1400px instead of lg's 1200px (Robert's pick, 2026-08-26).
  const isWideModal = funcModal === 'heybro_insurance';

  const renderDashboard = () => (
    <Container maxWidth="lg" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', py: 0, px: { xs: 2, md: 3 } }}>
      {/* ─── Header ─── */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'transparent', mb: 2 }}>
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: { xs: 64 }, py: 1.5 }}>
          {/* Bank logo + name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              component="img"
              src={bankLogoSrc || earlylionLogoSrc}
              alt={theme.palette.bank?.name || 'EarlyLion'}
              sx={{ height: { xs: 28, md: 32 }, width: 'auto' }}
            />
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: 15, md: 18 }, letterSpacing: '0.2px', whiteSpace: 'nowrap' }}>
              Integraal Bank
            </Typography>
          </Box>

          {/* Controls cluster */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              size="small"
              renderValue={(value) => (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, lineHeight: 1 }}>
                  <Box
                    component={FontAwesomeIcon}
                    icon={faGlobe}
                    sx={{ fontSize: 13 }}
                  />
                  {String(value).slice(0, 2).toUpperCase()}
                </Box>
              )}
              sx={{
                // Globe, code text and the dropdown arrow all inherit this single
                // colour, so they match each other and the other top-bar icons
                // (same resting colour, primary.main on hover).
                color: TOP_BAR_ICON_COLOR,
                fontSize: '12px',
                fontWeight: 600,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: '10px',
                mr: '4px',
                '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.5, pl: 1.25, pr: '24px !important', minWidth: 0, color: 'inherit' },
                '& .MuiSelect-icon': { right: 2, fontSize: 18, color: 'inherit' },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                transition: 'color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
                '&:hover': { color: 'primary.main', borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              <MenuItem value="de">DE</MenuItem>
              <MenuItem value="en">EN</MenuItem>
              <MenuItem value="fr">FR</MenuItem>
              <MenuItem value="es">ES</MenuItem>
              <MenuItem value="it">IT</MenuItem>
            </Select>
            {uiConfig.show_breaches_button && (
              <Tooltip title="Breaches">
                <IconButton onClick={handleModalOpen} size="small" sx={TOP_BAR_ICON_SX}>
                  <WarningAmberIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={getUser()?.is_admin ? 'User management' : 'My profile'}>
              <IconButton onClick={() => setShowUserMgmt(true)} size="small" sx={TOP_BAR_ICON_SX}>
                <ManageAccountsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Logout">
              <IconButton onClick={logout} size="small" sx={TOP_BAR_ICON_SX}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ─── Hero (compact) ─── */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4, mt: 1, textAlign: 'center' }}>
          {uiConfig.hero_image ? (
            <Box
              component="img"
              src={uiConfig.hero_image}
              alt=""
              sx={{
                mb: 2,
                width: '100%',
                maxHeight: { xs: 140, md: 200 },
                objectFit: 'cover',
                borderRadius: 3,
                display: 'block',
              }}
            />
          ) : (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box component="img" src={heybroLogoSrc} alt="HeyBro Finance" sx={{ width: { xs: 44, md: 56 }, height: 'auto' }} />
            </Box>
          )}
          <Typography
            sx={{
              color: 'text.secondary',
              fontWeight: 300,
              fontSize: { xs: '15px', md: '18px' },
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {t('greeting')} {firstName || username}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', opacity: 0.7 }}>
            {today}
          </Typography>
        </Box>
      </Fade>

      {/* Breaches Modal */}
      {uiConfig.show_breaches_button && (
        <BreachesModal
          open={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          onExpandChat={(component, prefillText) => {
            if (component === 'chat') {
              setChatPrefill(prefillText);
              setFuncModal('chat');
            }
          }}
        />
      )}

      {/* ─── Own audio cards (src/audio/) ─── */}
      {hasAudioCards && <AudioCards language={i18n.language} spaced={funcTypes.length > 0} />}

      {/* ─── Function tiles ─── */}
      {funcTypes.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
          {funcTypes.map((type, index) => {
            const cfg = FUNCTION_TILES[type];
            return (
              <ButtonBase
                key={type}
                onClick={() => setFuncModal(type)}
                className="card-enter"
                sx={{
                  animationDelay: `${index * 80}ms`,
                  width: '100%',
                  justifyContent: 'flex-start',
                  gap: 2,
                  p: 2,
                  borderRadius: '14px',
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  transition: 'border-color 0.2s ease, background-color 0.2s ease',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    bgcolor: 'rgba(25, 208, 239, 0.08)',
                    color: 'primary.main',
                    '& .MuiSvgIcon-root': { fontSize: 24 },
                  }}
                >
                  {cfg.icon}
                </Box>
                <Typography sx={{ fontWeight: 500, fontSize: '15px', letterSpacing: '0.02em' }}>
                  {t(cfg.titleKey)}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      )}

      {/* ─── Footer (always pinned to the bottom) ─── */}
      <Box component="footer" sx={{ mt: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', pt: 5, pb: 4 }}>
        <ThemeSwitcher />
      </Box>

      {/* ─── Function modal ─── */}
      <Dialog
        open={!!funcModal}
        onClose={closeOnlyViaButton(closeFuncModal)}
        maxWidth={isWideModal ? false : 'lg'}
        fullWidth
        sx={{ '& .MuiDialog-container': { alignItems: 'center', justifyContent: 'center' } }}
        PaperProps={{ sx: { borderRadius: '14px', maxHeight: '92vh', overflow: 'hidden', ...(isChatModal && { height: { xs: '92vh', md: '88vh' } }), ...(isWideModal && { maxWidth: 1400 }) } }}
      >
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', maxHeight: '92vh', ...(isChatModal && { height: '100%' }) }}>
          {/* Every hosted modal renders its own header with a Close button
              (aligned with its title), so there is no floating close here. */}
          <Box sx={{ flex: isChatModal ? 1 : 'unset', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: isChatModal ? 'hidden' : 'auto', p: { xs: 1.5, md: 2 } }}>
            {funcModal === 'chat' && (
              <ExpandableAssistantLion
                userName={username}
                title={t('chat')}
                icon={<InsightsIcon />}
                modal
                expanded={true}
                sessionId={sessionId}
                language={i18n}
                resetSessionId={resetSessionId}
                chatPrefill={chatPrefill}
                onCloseChat={closeFuncModal}
                onOpenChat={() => setFuncModal('chat')}
                onResetMessages={() => setChatPrefill('')}
              />
            )}
            {funcModal === 'client_notes' && (
              <ExpandableClient
                title={t('customer_note')}
                icon={<GroupsIcon />}
                modal
                expanded={true}
                sessionId={sessionId}
                language={i18n}
                onClose={closeFuncModal}
              />
            )}
            {funcModal === 'meeting_minutes' && (
              <ExpandableMeetingMinutes
                title={t('meeting_minutes') || 'Meeting Minutes'}
                icon={<DescriptionIcon />}
                modal
                expanded={true}
                language={i18n.language}
                firstName={firstName}
                onClose={closeFuncModal}
              />
            )}
            {funcModal === 'advisor_reasoning' && (
              <ExpandableAdvisorReasoning
                language={i18n.language}
                onClose={closeFuncModal}
              />
            )}
            {funcModal === 'efd_agent' && (
              <EfdAgentChat language={i18n.language} userName={username} onClose={closeFuncModal}
                agent={efdAgentFor(assistants?.efd?.variant)} />
            )}
            {funcModal === 'ppi_agent' && (
              <PpiAgentChat language={i18n.language} userName={username} onClose={closeFuncModal} />
            )}
            {funcModal === 'heybro_insurance' && (
              <HeyBroInsurance onClose={closeFuncModal} />
            )}
          </Box>
        </Box>
      </Dialog>

      {/* ─── Floating chat widget ─── */}
      {hasChatWidget && <ChatWidget language={i18n.language} />}
    </Container>
  );

  return (
    <Router>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Routes>
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/" /> : <Login onLogin={handleLogin} notice={sessionExpired ? t('session_expired') : ''} />}
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                showUserMgmt ? (
                  <UserManagement onBack={() => setShowUserMgmt(false)} onUserUpdated={handleUserUpdated} />
                ) : renderDashboard()
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </Box>
    </Router>
  );
}

export default App;
